// @flow
import { Map } from 'immutable';
import { actionPredicate, parseActionTypeAndState } from './index';
import { SpaceRecord } from '../../records';
import Constants from '../../constants';
import type { Action } from '../../actions/types';
import { ALL_FETCH_TOPIC_TYPES } from '../../constants';

/**
 * Updates reducer state according to result of transformation.
 *
 * @param state Immutable state to update
 * @param action action type
 * @param entityKey entity to transform
 * @param recordType Record to transform to
 * @returns {*}
 */
export const mergeEntitiesIntoState = (
	state: any,
	action: Action,
	entityKey: string,
	recordType: string,
) => {
	const { payload } = action;

	//  Ensure we do not return a broken state.
	if (!payload) {
		return state;
	}
	//  If action succeeded we map our data to records and then merge.
	const returnState =
		action.succeeded || action.statusUndefined
			? state.merge(mapToRecords(payload.results.entities[entityKey], recordType))
			: state;

	if (action.succeeded && ALL_FETCH_TOPIC_TYPES.has(action.actionType)) {
		// If we have a topic from Confluence Cloud and Local DB, we need to remove the local one from the list.
		const confluenceCloudTitles = returnState
			.toSet()
			.filter((value) => value.confluenceCloud)
			.map((value) => value.title);
		return returnState.filter((value) => {
			return value.confluenceCloud || !confluenceCloudTitles.has(value.title);
		});
	}

	return returnState;
};

export const removeEntityFromState = (state: any, action: Action) =>
	action.succeeded ? state.delete(action.params.entityId) : state;

/**
 * Takes a normalized payload and converts to Immutable Records.
 *
 * @param targetObject JSON map of Normalizr entities
 * @param RecordType Record to convert each Normalizr entity to
 * @returns {*}
 */
export const mapToRecords = (targetObject: any, RecordType: any) => {
	if (!targetObject || !RecordType) return Map({});

	const target = {};
	for (const key of Object.keys(targetObject)) {
		target[key] = new RecordType(targetObject[key]);
	}
	return Map(target);
};

/**
 * Generates a transformer which converts normalized payloads to Immutable Records.
 *
 * @param acceptedActions actions for which this transformer is to be applied
 * @param entityKey entity to be transformed
 * @param recordType record to be converted to
 * @param defaultState default state
 */
export const reducerMerger =
	(
		acceptedActions: Array<string>,
		entityKey: string,
		recordType: string,
		defaultState: Map<string, any> = Map(),
	) =>
	(state: Map<string, any> = defaultState, dispatchedAction: Action) => {
		const action = parseActionTypeAndState(dispatchedAction);

		return actionPredicate(action.actionType, acceptedActions)
			? mergeEntitiesIntoState(state, action, entityKey, recordType)
			: state;
	};

/**
 * Remove single entity from the state.
 *
 * @param acceptedActions list of accepted action types
 */
export const reducerRemover =
	(acceptedActions: Array<string>) =>
	(state: Map<string, any> = Map(), dispatchedAction: Action) => {
		const action = parseActionTypeAndState(dispatchedAction);

		return actionPredicate(action.actionType, acceptedActions)
			? removeEntityFromState(state, action)
			: state;
	};

/**
 * Updates reducer state according to result of transformation, with logic for deep merging SpaceRecords.
 * This allows entities to be populated from multiple sources (e.g. API requests) that each provide different
 * fields for the record.
 *
 * @param state Immutable state to update
 * @param action action type
 * @param entityKey entity to transform
 * @param recordType Record to transform to
 * @returns {*}
 */
export const mergeSpaceEntitiesIntoState = (
	state: Map<string, any>,
	action: Action,
	entityKey: string,
	recordType: string,
) => {
	const { payload } = action;

	//  Ensure we do not return a broken state.
	if (!payload) {
		return state;
	}

	//  If action succeeded we map our data to records and then merge.
	if (action.succeeded || action.statusUndefined) {
		const records = mapToRecords(payload.results.entities[entityKey], recordType);

		return state.mergeWith((prev: ?SpaceRecord, next: ?SpaceRecord) => {
			if (!next) {
				return prev;
			}

			if (!prev) {
				return next;
			}

			const spaceKey: string = next.spaceKey ? next.spaceKey : prev.spaceKey;
			const name: string = next.name ? next.name : prev.name;
			const watching: boolean = next.watching !== null ? next.watching : prev.watching;

			return new SpaceRecord({ spaceKey, name, watching });
		}, records);
	} else {
		return state;
	}
};

/**
 * Generates a transformer which converts normalized payloads to Immutable Records.  Specific to
 * spaces, so that space records can be merged rather than the new record overwriting the old record
 * entirely.
 *
 * @param acceptedActions actions for which this transformer is to be applied
 * @param entityKey entity to be transformed
 * @param recordType record to be converted to
 * @param defaultState
 */
export const spaceReducerMerger =
	(
		acceptedActions: Array<string>,
		entityKey: string,
		recordType: string,
		defaultState: Map<string, any> = Map(),
	) =>
	(state: Map<string, any> = defaultState, dispatchedAction: Action) => {
		const action: Action = parseActionTypeAndState(dispatchedAction);

		return actionPredicate(action.actionType, acceptedActions)
			? mergeSpaceEntitiesIntoState(state, action, entityKey, recordType)
			: state;
	};

/**
 * Update vote count property in the right content (question or answer).
 *
 * @param acceptedActions list of accepted action types
 */
export const reducerUpdateVoteCountInContent =
	(acceptedActions: Array<string>) =>
	(state: Map<string, any> = Map(), dispatchedAction: Action) => {
		const action = parseActionTypeAndState(dispatchedAction);

		if (actionPredicate(action.actionType, acceptedActions)) {
			if (action.succeeded) {
				const content = state.get(action.params.contentId);
				if (!content) {
					return state;
				}
				const increment =
					action.actionType === Constants.VOTE_DOWN
						? -1
						: action.actionType === Constants.VOTE_UP
							? 1
							: 0;
				const newContent = content.set('votes', content.get('votes') + increment);
				return state.set(action.params.contentId, newContent);
			}
			return state;
		}
		return state;
	};

export const reducerUpdateWatching =
	(acceptedActions: Array<string>) =>
	(state: Map<string, any> = Map(), dispatchedAction: Action) => {
		const action = parseActionTypeAndState(dispatchedAction);

		if (actionPredicate(action.actionType, acceptedActions)) {
			const entity = state.get(action.params.entityId);
			if (action.succeeded && entity) {
				const modifiedEntity = entity.set('watching', action.payload.results.watching);
				return state.set(action.params.entityId, modifiedEntity);
			}
			return state;
		}
		return state;
	};
